'use client'

import React, { useEffect, useState } from 'react'
import { Box, Divider, Flex, Grid, Skeleton, Stack, Text, Title } from '@mantine/core'
import { LabDetailsData, LabSkuResponse, LabTestDetails } from '@/types/interfaces/sku'
import { useApi } from '@/contexts/ApiContext'
import { isMobile } from 'react-device-detect'

import IconAlarmClock from '../assets/icons/icon-alarm-clock.svg'
import IconBarbell from '../assets/icons/icon-barbell.svg'
import IconDrop from '../assets/icons/icon-drop.svg'
import IconFlame from '../assets/icons/icon-flame.svg'
import IconHeart from '../assets/icons/icon-heart.svg'
import IconIron from '../assets/icons/icon-iron.svg'
import IconKidney from '../assets/icons/icon-kidney.svg'
import IconShield from '../assets/icons/icon-shield.svg'
import IconSugar from '../assets/icons/icon-sugar.svg'
import IconSun from '../assets/icons/icon-sun.svg'
import IconThyroid from '../assets/icons/icon-thyroid.svg'
import IconTube from '../assets/icons/icon-tube.svg'
import IconVirus from '../assets/icons/icon-virus.svg'
import IconWheat from '../assets/icons/icon-wheat.svg'

const iconMap: Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>> = {
    IconAlarmClock,
    IconBarbell,
    IconDrop,
    IconFlame,
    IconHeart,
    IconIron,
    IconKidney,
    IconShield,
    IconSugar,
    IconSun,
    IconThyroid,
    IconTube,
    IconVirus,
    IconWheat,
}

interface LabDetailsProps {
    skuId: string
    labDetails: LabDetailsData
    wellnessSku?: boolean
}

export function LabDetails({ skuId, labDetails }: LabDetailsProps) {
    const [test_details, setTestDetails] = useState<LabTestDetails[] | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const { apiGet } = useApi()
    const title = "What's included"

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const lab_options: LabSkuResponse = await apiGet(`/external/lab_skus/${skuId}`)
                setTestDetails(lab_options.sku.test_details)
                setIsLoading(false)
            } catch (error) {
                console.error('Failed to fetch lab details:', error)
                setIsLoading(false)
            }
        }
        fetchDetails().then((r) => r)
    }, [skuId, apiGet])

    if (isLoading) {
        return renderLoadingView()
    }

    if (isMobile) {
        return renderMobileView()
    } else {
        return renderWebView()
    }

    function renderWebView() {
        return (
            <Stack gap={54}>
                {renderTitle()}
                <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>{renderTestDetails()}</Grid.Col>

                    <Grid.Col span={{ base: 12, md: 6 }}>{renderPanelCards()}</Grid.Col>
                </Grid>
            </Stack>
        )
    }

    function renderMobileView() {
        return (
            <Stack gap={32}>
                {renderTitle()}
                {renderPanelCards()}
                {renderTestDetails()}
            </Stack>
        )
    }

    function renderTitle() {
        return (
            <Stack gap={30}>
                <Title
                    order={1}
                    fz={isMobile ? 36 : 48}
                    fw={isMobile ? 500 : 400}
                    c={'var(--GM-Text-Black, #181818)'}
                    lh={isMobile ? '40px' : '64px'}
                    lts={isMobile ? '-0.72px' : '-1.08px'}
                >
                    {title}
                </Title>
                {!isMobile && <Divider bd={'1px solid var(--gm-foreground-fg-outline-primary)'} />}
            </Stack>
        )
    }

    function renderPanelCards() {
        return (
            <Stack gap={30}>
                {test_details?.map((test: LabTestDetails, index) => {
                    const shouldRenderDivider = index !== test_details.length - 1 || isMobile
                    return (
                        <React.Fragment key={index}>
                            {renderPanelCard(test)}
                            {shouldRenderDivider && <Divider size="md" />}
                        </React.Fragment>
                    )
                })}
            </Stack>
        )
    }

    function renderPanelCard(test: LabTestDetails) {
        const IconComponent = iconMap[test.icon] || IconDrop
        return (
            <Flex align={'flex-start'} justify={'center'} gap={20} maw={isMobile ? '100%' : 550}>
                <IconComponent width={40} height={40} />
                <Stack flex={1} gap={14}>
                    <Text fz={20} fw={400} c={'var(--GM-Text-Black, #181818)'} lh={'23px'}>
                        {test.name}
                    </Text>
                    <Text fz={18} fw={400} c={'var(--GM-Text-Gray, #6E6E68)'} lh={'23px'}>
                        {test.description}
                    </Text>{' '}
                    {!isMobile && test.includes && (
                        <Text fz={18} fw={400} lh={'23px'} c={'var(--GM-Text-Gray, #6E6E68)'}>
                            <i>Includes: {test.includes}</i>
                        </Text>
                    )}
                </Stack>
            </Flex>
        )
    }

    function renderTestDetails() {
        return (
            <Stack gap={28}>
                {renderTestDetailBlock('Test type', labDetails?.test_type)}
                {renderTestDetailBlock('Collection method', labDetails?.collection_method)}
                {renderTestDetailBlock('Fasting', labDetails?.fasting ? 'Required' : 'Not required')}
                {renderTestDetailBlock('Results processing time', labDetails?.results_processing_time)}
            </Stack>
        )
    }

    function renderTestDetailBlock(title: string, detail: string) {
        return (
            <Stack gap={6}>
                <Text fz={20} fw={500} c={'var(--GM-Text-Black, #181818)'} lh={'21px'}>
                    {title}
                </Text>
                <Text fz={18} fw={400} c={'var(--GM-Text-Black, #181818)'} lh={'23px'}>
                    {detail}
                </Text>
            </Stack>
        )
    }

    function renderLoadingView() {
        return (
            <Stack gap={32}>
                {renderTitle()}
                {isMobile ? (
                    <>
                        {renderSkeletonPanels()}
                        {renderSkeletonTestDetails()}
                    </>
                ) : (
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 6 }}>{renderSkeletonTestDetails()}</Grid.Col>

                        <Grid.Col span={{ base: 12, md: 6 }}>{renderSkeletonPanels()}</Grid.Col>
                    </Grid>
                )}
            </Stack>
        )
    }

    function renderSkeletonTestDetails() {
        return (
            <Stack gap={28}>
                <Stack gap={6}>
                    <Text fz={20} fw={500} c={'var(--GM-Text-Black, #181818)'} lh={'21px'}>
                        Test type
                    </Text>
                    <Skeleton height={18} w={isMobile ? '100%' : 300} />
                </Stack>
                <Stack gap={6}>
                    <Text fz={20} fw={500} c={'var(--GM-Text-Black, #181818)'} lh={'21px'}>
                        Collection method
                    </Text>
                    <Skeleton height={18} w={isMobile ? '100%' : 300} />
                </Stack>
                <Stack gap={6}>
                    <Text fz={20} fw={500} c={'var(--GM-Text-Black, #181818)'} lh={'21px'}>
                        Fasting
                    </Text>
                    <Skeleton height={18} w={isMobile ? '100%' : 300} />
                </Stack>
                <Stack gap={6}>
                    <Text fz={20} fw={500} c={'var(--GM-Text-Black, #181818)'} lh={'21px'}>
                        Results processing time
                    </Text>
                    <Skeleton height={18} w={isMobile ? '100%' : 300} />
                </Stack>
            </Stack>
        )
    }

    function renderSkeletonPanels() {
        return (
            <Stack gap={30}>
                {Array.from({ length: 5 }).map((_value, index) => {
                    return (
                        <Box key={'skeleton-panel-' + index}>
                            <Flex align={'flex-start'} justify={'center'} gap={20} maw={isMobile ? '100%' : 550}>
                                <Skeleton height={40} w={40} />
                                <Stack flex={1} gap={14}>
                                    <Skeleton height={20} />
                                    <Skeleton height={18} />
                                    {!isMobile && <Skeleton height={18} />}
                                </Stack>
                            </Flex>
                            {<Divider size="md" />}
                        </Box>
                    )
                })}
            </Stack>
        )
    }
}
